import { apiObject } from "rudder-sdk-js";
import { useEffect, useRef } from "react";
import { useRouter } from "next/router";

import { getPosthog, initializeAnalytics } from "@/utils/analytics";

interface Capture {
  page: string;
  properties: apiObject;
}

/**
 * Hook to be called in the body of your dynamic page's
 * functionnal component.
 *
 * @example
 * ```
 * captureDynamicPageView("/[artist]/[track]", {
 *  artist: track.artist.slug,
 *  track: track.slug,
 * });
 * ```
 *
 * Will only call `getPosthog()?.capture` once per page + properties
 * combination. Will call again if either changes.
 *
 * Automatically adds all of `router.query` into event properties
 *
 * @remarks properties' diff is done only 1 level deep.
 * This means nested objects will aways trigger a new capture.
 *
 * @param page - Name of the page (with placeholders)
 * e.g. /[artist]/[track]
 * @param properties - optional posthog capture event properties
 */
export default function useCaptureDynamicPageView(page: string, properties?: apiObject) {
  const router = useRouter();

  const lastCapture = useRef<Capture>();

  useEffect(() => {
    const propertiesWithQuery = {
      ...router.query,
      ...properties,
    };
    const capture: Capture = {
      page,
      properties: propertiesWithQuery,
    };

    for (const x in capture.properties) {
      if (capture.properties[x] !== lastCapture.current?.properties[x]) {
        initializeAnalytics().then((analytics) => {
          getPosthog()?.register({ "Pianity URL": page });

          analytics?.track(`Viewed ${page} page`, propertiesWithQuery);
          analytics?.page();
        });
        lastCapture.current = capture;
        break;
      }
    }
  }, [page, properties, router.query]);
}
